import { cleanDescription } from "../../lib/description";
import { useQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import AnalysisTableState from "./AnalysisTableState";
import type { RefObject } from "react";
import { Link } from "react-router-dom";

import type { ProjectSummary } from "../../api/projects";
import { fetchPriceVariance } from "../../api/reports";
import type {
  PriceVarianceFilters,
  PriceVarianceRow,
  VarianceGroupBy,
  VarianceGroupedSort,
  VarianceGroupRow,
  VarianceSort,
} from "../../api/reports";
import {
  TableLoadMoreFooter,
} from "../../components/DataTable";
import Spinner from "../../components/Spinner";
import {
  costCenterListHref,
  deliveryAddressCostCenterHref,
  projectCostCenterHref,
} from "../../lib/costCenterDisplay";
import { belegReviewHref } from "../../lib/belegReviewLinks";
import {
  formatDate,
  formatDateShort,
  formatEuro,
  formatQty,
  toNumber,
  formatSignedPercent,
} from "../../lib/format";
import { projectDisplay } from "../../lib/projectDisplay";
import {
  DELTA_MARKT_TOOLTIP,
  FLAT_SORT_PAIRS,
} from "./constants";
export function VarianceFlatTable(args: {
  items: PriceVarianceRow[];
  hasData: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  onAdjustFilters: (opener: HTMLButtonElement) => void;
  total: number | null;
  hasMore: boolean;
  onLoadMore: () => void;
  loadMoreRef: RefObject<HTMLDivElement | null>;
  projectByKey: ReadonlyMap<string, ProjectSummary> | undefined;
  sort: VarianceSort;
  onSortChange: (next: VarianceSort) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  fromPath: string;
  insufficientReferences?: boolean;
}) {
  const {
    items,
    hasData,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    onRetry,
    onAdjustFilters,
    total,
    hasMore,
    onLoadMore,
    loadMoreRef,
    projectByKey,
    sort,
    onSortChange,
    scrollRef,
    fromPath,
    insufficientReferences = false,
  } = args;

  if ((isFetching && !isFetchingNextPage) || !hasData || isError || !items.length) {
    return <AnalysisTableState loading={(isFetching && !isFetchingNextPage) || (!hasData && !isError)} error={isError ? error : null} onRetry={onRetry} onAdjustFilters={onAdjustFilters} />;
  }
  const showMeta = hasData && !isError;
  const totalLabel = total ?? items.length;

  return (
    <>
      <div className="mb-2 flex items-baseline justify-between text-xs text-slate-500">
        <span className="flex items-center gap-2">
          {showMeta
            ? `${items.length.toLocaleString("de-AT")} von ${totalLabel.toLocaleString("de-AT")} Zeilen`
            : null}
          {isFetching && !isFetchingNextPage && showMeta ? (
            <Spinner size="sm" label="Aktualisiere …" />
          ) : null}
        </span>
      </div>
      <div
        ref={scrollRef}
        className="relative overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm"
      >
        <table className="w-full min-w-[920px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <SortableTh
                label="Datum"
                pair={FLAT_SORT_PAIRS.doc_date}
                sort={sort}
                onChange={onSortChange}
                sticky
              />
              <th className="px-3 py-2">Artikel</th>
              <th className="px-3 py-2">Projekt</th>
              <th className="px-3 py-2 text-right">Menge</th>
              <SortableTh
                label="Netto"
                pair={FLAT_SORT_PAIRS.net_price}
                sort={sort}
                onChange={onSortChange}
                align="right"
              />
              <th
                className="whitespace-nowrap px-3 py-2 text-right"
                title="Preisreferenz zum Belegdatum: frühere 12 Monate, sonst 24 Monate, danach die gesamte frühere Historie."
                aria-label="Referenzbasis"
              >
                Referenz
              </th>
              <th
                className="whitespace-nowrap px-3 py-2 text-right"
                title={DELTA_MARKT_TOOLTIP}
              >
                Abweichung
              </th>
              <SortableTh
                label={insufficientReferences ? "Unterschied" : "Mehrkosten"}
                pair={FLAT_SORT_PAIRS.overpay}
                sort={sort}
                onChange={onSortChange}
                align="right"
                title="Positive Abweichung gegen die kanonische Referenz × Menge."
              />
              <th className="px-3 py-2"><span className="sr-only">Beleg</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((row, idx) => {
                const deltaPct = toNumber(row.delta_pct);
                const toneClass =
                  deltaPct === null
                    ? "text-slate-600"
                    : deltaPct > 0
                      ? "text-rose-700"
                      : "text-emerald-700";
                const project = row.project_key
                  ? projectByKey?.get(row.project_key)
                  : undefined;
                const display = projectDisplay(project, row.project_key);
                const buyer = row.online_user_name ?? row.online_user_email ?? null;
                const addressLabel = row.delivery_address_display ?? null;
                const projectTitleParts = [
                  display?.tooltip ?? null,
                  buyer ? `Besteller: ${buyer}` : null,
                  addressLabel
                    ? `Lieferadresse: ${addressLabel}${row.delivery_is_warehouse ? " (Lager)" : ""}`
                    : null,
                ].filter(Boolean) as string[];
                return (
                  <tr
                    key={`${row.invoice_doc_id ?? "doc"}-${row.line_pos ?? idx}`}
                    className="group hover:bg-slate-50"
                  >
                    <td
                      className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-2 tabular-nums shadow-[1px_0_0_0_rgb(226,232,240)] group-hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-2">
                        {formatDateShort(row.doc_date)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {/* Eine Zeile: Bezeichnung; Nummer und Hersteller im Tooltip (#630). */}
                      <Link
                        to={`/articles/${encodeURIComponent(row.article_group_key)}`}
                        state={{ from: fromPath }}
                        className="block w-64 max-w-64 truncate text-slate-900 hover:text-emerald-700 hover:underline"
                        title={[row.description, row.primary_article_code, row.brand_name].filter(Boolean).join(" · ")}
                      >
                        {row.description ? cleanDescription(row.description) : (row.primary_article_code ?? row.article_group_key)}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.project_key ? (
                        display ? (
                          <Link
                            to={
                              projectCostCenterHref(row.project_key) ??
                              costCenterListHref("project")
                            }
                            state={{ from: fromPath }}
                            className="block w-32 max-w-32 text-emerald-700 hover:underline"
                            title={projectTitleParts.join("\n") || undefined}
                          >
                            <span className="block truncate font-sans text-sm">
                              {display.title}
                            </span>
                          </Link>
                        ) : (
                          <span
                            className="block w-32 max-w-32 truncate text-slate-700"
                            title={projectTitleParts.join("\n") || undefined}
                          >
                            {row.project_key}
                          </span>
                        )
                      ) : (
                        <span
                          className="text-slate-500"
                          title={projectTitleParts.join("\n") || undefined}
                        >
                          –
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatQty(row.qty)}
                      {row.uom ? (
                        <span className="ml-1 text-xs text-slate-500">{row.uom}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {formatEuro(row.net_unit_price)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      <ReferenceEvidence row={row} />
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums font-medium ${toneClass}`}
                      title={DELTA_MARKT_TOOLTIP}
                    >
                      {formatSignedPercent(row.delta_pct)}
                    </td>
                    <td
                      className="px-3 py-2 text-right font-medium tabular-nums text-slate-900"
                      title={`Abweichung gegen ${baselineKindLabel(row.canonical_baseline_kind)}: ${formatSignedPercent(row.delta_pct)} · ${DELTA_MARKT_TOOLTIP}`}
                    >
                      {formatEuro(row.overpay_abs)}
                    </td>
                    <td className="px-3 py-2">
                      <VarianceRowActions row={row} returnTo={fromPath} />
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </div>

      {showMeta && items.length > 0 ? (
        <div className="mt-3 flex justify-center text-xs text-slate-500">
          <TableLoadMoreFooter
            loadMoreRef={loadMoreRef}
            loadedCount={items.length}
            totalCount={total}
            itemLabel="Zeilen"
            hasMore={hasMore}
            isLoading={isFetchingNextPage}
            buttonLabel="Weitere Zeilen laden"
            loadingLabel="Lade weitere Zeilen …"
            onLoadMore={onLoadMore}
          />
        </div>
      ) : null}
    </>
  );
}

export function VarianceGroupedTable(args: {
  data: { group_by: string[]; items: VarianceGroupRow[] } | undefined;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  onAdjustFilters: (opener: HTMLButtonElement) => void;
  axes: VarianceGroupBy;
  expanded: Set<string>;
  toggleGroupRow: (key: string) => void;
  groupKeyFor: (row: VarianceGroupRow) => string;
  filters: PriceVarianceFilters;
  flatSort: VarianceSort;
  projectByKey: ReadonlyMap<string, ProjectSummary> | undefined;
  sort: VarianceGroupedSort;
  onSortChange: (next: VarianceGroupedSort) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  fromPath: string;
}) {
  const {
    data,
    isFetching,
    isError,
    error,
    onRetry,
    onAdjustFilters,
    axes,
    expanded,
    toggleGroupRow,
    groupKeyFor,
    filters,
    flatSort,
    projectByKey,
    sort,
    onSortChange,
    scrollRef,
    fromPath,
  } = args;

  const showManufacturer = axes.includes("manufacturer");
  const showProject = axes.includes("project");
  const showAddress = axes.includes("delivery_address");
  // Erste Achse links sticky (das ist das Drilldown-Anker-Feld).
  const firstAxis = axes.split(",")[0];
  const axisColCount = (showManufacturer ? 1 : 0) + (showProject ? 1 : 0) + (showAddress ? 1 : 0);

  // Totals quer über alle gerenderten Gruppen — entspricht der gefilterten
  // Grundgesamtheit, weil das Backend die Aggregation schon dimensional
  // ausführt.
  const totals = (data?.items ?? []).reduce(
    (acc, row) => ({
      lines: acc.lines + Number(row.line_count ?? 0),
      spend: acc.spend + Number(row.total_spend ?? 0),
      overpay: acc.overpay + Number(row.total_overpay ?? 0),
    }),
    { lines: 0, spend: 0, overpay: 0 },
  );
  if (isFetching || !data || isError || !data.items.length) {
    return <AnalysisTableState loading={isFetching || (!data && !isError)} error={isError ? error : null} onRetry={onRetry} onAdjustFilters={onAdjustFilters} />;
  }
  const showMeta = data !== undefined && !isError;

  return (
    <>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-xs text-slate-500">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {showMeta ? (
            <>
              <span>{data!.items.length.toLocaleString("de-AT")} Gruppen</span>
              <strong className="font-semibold text-slate-700">
                Mehrkosten Auswahl {formatEuro(totals.overpay)}
              </strong>
              <span>Summe Ausgaben {formatEuro(totals.spend)}</span>
              <span>{totals.lines.toLocaleString("de-AT")} Zeilen</span>
              {isFetching ? (
                <Spinner size="sm" label="Aktualisiere …" />
              ) : null}
            </>
          ) : null}
        </span>
      </div>
      <div
        ref={scrollRef}
        className="relative overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm"
      >
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {showManufacturer ? (
                <th
                  className={`px-3 py-2 ${
                    firstAxis === "manufacturer" ? "sticky left-0 z-10 bg-slate-50" : ""
                  }`}
                >
                  Hersteller
                </th>
              ) : null}
              {showProject ? (
                <th
                  className={`px-3 py-2 ${
                    firstAxis === "project" ? "sticky left-0 z-10 bg-slate-50" : ""
                  }`}
                >
                  Projekt
                </th>
              ) : null}
              {showAddress ? (
                <th
                  className={`px-3 py-2 ${
                    firstAxis === "delivery_address" ? "sticky left-0 z-10 bg-slate-50" : ""
                  }`}
                >
                  Lieferadresse
                </th>
              ) : null}
              <GroupedSortableTh
                label="Zeilen"
                sortKey="lines_desc"
                sort={sort}
                onChange={onSortChange}
                align="right"
              />
              <GroupedSortableTh
                label="Ausgaben"
                sortKey="spend_desc"
                sort={sort}
                onChange={onSortChange}
                align="right"
              />
              <GroupedSortableTh
                label="Mehrkosten"
                sortKey="overpay_desc"
                sort={sort}
                onChange={onSortChange}
                align="right"
                title="Summe der positiven Abweichung × Menge"
              />
              <th
                className="px-3 py-2 text-right"
                title={DELTA_MARKT_TOOLTIP}
              >
                Abw. Referenz %
              </th>
              <th className="px-3 py-2 text-right">Zeitraum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map((row) => {
              const key = groupKeyFor(row);
              const isOpen = expanded.has(key);
              const project = row.project_key
                ? projectByKey?.get(row.project_key)
                : undefined;
              const display = projectDisplay(project, row.project_key);
              const deliveryAddressHref = deliveryAddressCostCenterHref(
                row.delivery_address_key,
              );
              const deliveryAddressLabel =
                row.delivery_address_display ??
                row.delivery_address_key ??
                "unbekannt";
              return (
                <Fragment key={key}>
                  <tr
                    className="group cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleGroupRow(key)}
                  >
                    {showManufacturer ? (
                      <td
                        className={`px-3 py-2 ${
                          firstAxis === "manufacturer"
                            ? "sticky left-0 z-10 bg-white shadow-[1px_0_0_0_rgb(226,232,240)] group-hover:bg-slate-50"
                            : ""
                        }`}
                      >
                        {firstAxis === "manufacturer" ? (
                          <span className="mr-1 inline-block w-3 text-slate-400">
                            {isOpen ? "▾" : "▸"}
                          </span>
                        ) : null}
                        {row.manufacturer_id ? (
                          <span className="font-medium text-slate-700">
                            {row.manufacturer_name ?? row.manufacturer_id}
                          </span>
                        ) : (
                          <span className="text-slate-400">– ohne Hersteller</span>
                        )}
                      </td>
                    ) : null}
                    {showProject ? (
                      <td
                        className={`min-w-0 px-3 py-2 text-xs ${
                          firstAxis === "project"
                            ? "sticky left-0 z-10 bg-white shadow-[1px_0_0_0_rgb(226,232,240)] group-hover:bg-slate-50"
                            : ""
                        }`}
                      >
                        {firstAxis === "project" ? (
                          <span className="mr-1 inline-block w-3 text-slate-400">
                            {isOpen ? "▾" : "▸"}
                          </span>
                        ) : null}
                        {display ? (
                          <span
                            className="inline-block max-w-[14rem] align-bottom"
                            title={display.tooltip}
                          >
                            <span className="block truncate font-sans text-sm font-medium text-slate-700">
                              {display.title}
                            </span>
                            {display.meta ? (
                              <span className="block truncate font-mono text-xs text-slate-500">
                                {display.meta}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-slate-400">– ohne Projekt</span>
                        )}
                      </td>
                    ) : null}
                    {showAddress ? (
                      <td
                        className={`px-3 py-2 ${
                          firstAxis === "delivery_address"
                            ? "sticky left-0 z-10 bg-white shadow-[1px_0_0_0_rgb(226,232,240)] group-hover:bg-slate-50"
                            : ""
                        }`}
                      >
                        {firstAxis === "delivery_address" ? (
                          <span className="mr-1 inline-block w-3 text-slate-400">
                            {isOpen ? "▾" : "▸"}
                          </span>
                        ) : null}
                        {deliveryAddressHref ? (
                          <Link
                            to={deliveryAddressHref}
                            state={{ from: fromPath }}
                            onClick={(event) => event.stopPropagation()}
                            className="font-medium text-slate-900 hover:underline"
                          >
                            {deliveryAddressLabel}
                          </Link>
                        ) : (
                          <span>{deliveryAddressLabel}</span>
                        )}
                        {row.delivery_is_warehouse ? (
                          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">
                            Lager
                          </span>
                        ) : null}
                      </td>
                    ) : null}
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatQty(row.line_count)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatEuro(row.total_spend)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                      {formatEuro(row.total_overpay)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatSignedPercent(row.avg_delta_pct)}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-500 tabular-nums">
                      {formatDate(row.first_doc_date)} → {formatDate(row.last_doc_date)}
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr>
                      <td
                        colSpan={axisColCount + 5}
                        className="bg-slate-50 px-3 py-2"
                      >
                        <GroupDetailLines
                          baseFilters={filters}
                          group={row}
                          sort={flatSort}
                          projectByKey={projectByKey}
                          fromPath={fromPath}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
              })
            }
          </tbody>
          {data && data.items.length > 0 ? (
            <tfoot className="border-t border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <td
                  colSpan={axisColCount}
                  className="px-3 py-2 text-left font-semibold"
                >
                  Auswahl
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatQty(totals.lines)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(totals.spend)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                  {formatEuro(totals.overpay)}
                </td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </>
  );
}

function GroupDetailLines(props: {
  fromPath: string;
  baseFilters: PriceVarianceFilters;
  group: VarianceGroupRow;
  sort: VarianceSort;
  projectByKey: ReadonlyMap<string, ProjectSummary> | undefined;
}) {
  const { baseFilters, group, sort, projectByKey, fromPath } = props;
  const filters: PriceVarianceFilters = {
    ...baseFilters,
    ...(group.manufacturer_id
      ? { manufacturer_id: [group.manufacturer_id] }
      : {}),
    ...(group.project_key ? { project: [group.project_key] } : {}),
    ...(group.delivery_address_key
      ? { delivery_address_key: [group.delivery_address_key] }
      : {}),
  };
  const linesQuery = useQuery({
    queryKey: ["reports", "price-variance", "group-expand", filters, sort],
    queryFn: () =>
      fetchPriceVariance({ ...filters, sort, limit: 50, offset: 0 }),
  });

  if (linesQuery.isLoading) {
    return <Spinner size="sm" label="Lade Einzelbelege …" />;
  }
  if (linesQuery.isError) {
    return (
      <span className="text-xs text-rose-700">
        Fehler: {(linesQuery.error as Error).message}
      </span>
    );
  }
  const items = linesQuery.data?.items ?? [];
  if (items.length === 0) {
    return <span className="text-xs text-slate-500">Keine Belege.</span>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="text-slate-500">
          <tr>
            <th className="px-2 py-1 text-left">Datum</th>
            <th className="px-2 py-1 text-left">Artikel</th>
            <th className="px-2 py-1 text-left">Projekt</th>
            <th className="px-2 py-1 text-left">Besteller</th>
            <th className="px-2 py-1 text-right">Netto</th>
            <th
              className="px-2 py-1 text-center"
              aria-label="Referenzbasis"
            >
              Referenz
            </th>
            <th className="px-2 py-1 text-right" title={DELTA_MARKT_TOOLTIP}>
              Abweichung
            </th>
            <th className="px-2 py-1 text-right" title="Positive Abweichung gegen die kanonische Referenz × Menge">Mehrkosten</th>
            <th className="px-2 py-1 text-left">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, idx) => {
            const deltaPct = toNumber(row.delta_pct);
            const deltaToneClass =
              deltaPct === null
                ? "text-slate-600"
                : deltaPct > 0
                  ? "text-rose-700"
                  : "text-emerald-700";
            const project = row.project_key
              ? projectByKey?.get(row.project_key)
              : undefined;
            const display = projectDisplay(project, row.project_key);
            return (
              <tr
                key={`${row.invoice_doc_id ?? "doc"}-${row.line_pos ?? idx}`}
                className="border-t border-slate-200"
              >
                <td className="px-2 py-1 tabular-nums">{formatDate(row.doc_date)}</td>
                <td className="px-2 py-1">
                  <Link
                    to={`/articles/${encodeURIComponent(row.article_group_key)}`}
                    state={{ from: fromPath }}
                    className="font-mono text-emerald-700 hover:underline"
                  >
                    {row.primary_article_code ?? row.article_group_key}
                  </Link>
                </td>
                <td className="max-w-[10rem] px-2 py-1">
                  {row.project_key && display ? (
                    <Link
                      to={
                        projectCostCenterHref(row.project_key) ??
                        costCenterListHref("project")
                      }
                      state={{ from: fromPath }}
                      className="block truncate text-emerald-700 hover:underline"
                      title={display.tooltip}
                    >
                      {display.title}
                    </Link>
                  ) : (
                    "–"
                  )}
                </td>
                <td className="px-2 py-1">
                  {row.online_user_name ?? row.online_user_email ?? "–"}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {formatEuro(row.net_unit_price)}
                </td>
                <td className="min-w-40 px-2 py-1 tabular-nums">
                  <ReferenceEvidence row={row} />
                </td>
                <td
                  className={`px-2 py-1 text-right font-medium tabular-nums ${deltaToneClass}`}
                >
                  {formatSignedPercent(row.delta_pct)}
                </td>
                <td className="px-2 py-1 text-right font-medium tabular-nums text-slate-900">
                  {formatEuro(row.overpay_abs)}
                </td>
                <td className="min-w-48 px-2 py-1">
                  <VarianceRowActions
                    row={row}
                    returnTo={fromPath}
                    stopPropagation
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(linesQuery.data?.total ?? 0) > items.length ? (
        <p className="mt-1 text-xs text-slate-500">
          Zeige erste {items.length} von {linesQuery.data?.total} Belegen.
        </p>
      ) : null}
    </div>
  );
}

function VarianceRowActions({
  row,
  returnTo,
  stopPropagation = false,
}: {
  row: PriceVarianceRow;
  returnTo: string;
  stopPropagation?: boolean;
}) {
  return (
    // Original und Prüfung liegen im Beleg; hier nur der Weg dorthin (#630).
    <div className="whitespace-nowrap text-right" onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}>
      <PriceReviewOpenAction row={row} returnTo={returnTo} />
    </div>
  );
}

function PriceReviewOpenAction({
  row,
  returnTo,
}: {
  row: PriceVarianceRow;
  returnTo: string;
}) {
  const href = belegReviewHref({
    belegId: row.invoice_doc_id,
    linePos: row.line_pos,
    priceAnomalyStatus: row.price_anomaly_status,
    returnTo,
  });
  if (!href) {
    return (
      <span
        aria-label="Keine Preisprüfung verfügbar"
        className="text-xs text-slate-400"
        title="Für diese Position ist keine sichere Preisprüfung verfügbar."
      >
        –
      </span>
    );
  }
  const reviewLabel = `Preisprüfung für Beleg ${
    row.doc_number ?? row.invoice_doc_id ?? "unbekannt"
  }, Position ${String(row.line_pos)} öffnen`;
  return (
    <Link
      to={href}
      state={{ from: returnTo }}
      aria-label={reviewLabel}
      data-testid="price-review-open-action"
      className="inline-flex items-center whitespace-nowrap font-sans text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
      title={reviewLabel}
    >
      Beleg →
    </Link>
  );
}

function SortableTh({
  label,
  pair,
  sort,
  onChange,
  align = "left",
  sticky = false,
  title,
}: {
  label: string;
  pair: { asc: VarianceSort; desc: VarianceSort };
  sort: VarianceSort;
  onChange: (next: VarianceSort) => void;
  align?: "left" | "right";
  sticky?: boolean;
  title?: string;
}) {
  const isAsc = sort === pair.asc;
  const isDesc = sort === pair.desc;
  const isActive = isAsc || isDesc;
  const next: VarianceSort = isDesc ? pair.asc : pair.desc;
  const indicator = isAsc ? "▲" : isDesc ? "▼" : "↕";
  const stickyClass = sticky ? "sticky left-0 z-10 bg-slate-50" : "";
  return (
    <th
      className={`px-3 py-2 ${
        align === "right" ? "text-right" : "text-left"
      } ${stickyClass}`}
    >
      <button
        type="button"
        onClick={() => onChange(next)}
        title={title}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          isActive
            ? "text-emerald-700"
            : "text-slate-500 hover:text-slate-700"
        }`}
      >
        {label}
        <span className="text-[10px] opacity-70">{indicator}</span>
      </button>
    </th>
  );
}

function GroupedSortableTh({
  label,
  sortKey,
  sort,
  onChange,
  align = "left",
  title,
}: {
  label: string;
  sortKey: VarianceGroupedSort;
  sort: VarianceGroupedSort;
  onChange: (next: VarianceGroupedSort) => void;
  align?: "left" | "right";
  title?: string;
}) {
  const isActive = sort === sortKey;
  const indicator = isActive ? "▼" : "↕";
  return (
    <th
      className={`px-3 py-2 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      <button
        type="button"
        onClick={() => onChange(sortKey)}
        title={title}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          isActive
            ? "text-emerald-700"
            : "text-slate-500 hover:text-slate-700"
        }`}
      >
        {label}
        <span className="text-[10px] opacity-70">{indicator}</span>
      </button>
    </th>
  );
}

function baselineKindLabel(kind: "12m" | "24m" | "alltime" | null): string {
  switch (kind) {
    case "12m":
      return "frühere 12 Monate";
    case "24m":
      return "frühere 24 Monate";
    case "alltime":
      return "gesamte frühere Historie";
    default:
      return "keine frühere Referenz";
  }
}

function ReferenceEvidence({ row }: { row: PriceVarianceRow }) {
  const lineCount = toNumber(row.canonical_baseline_line_count);
  const documentCount = toNumber(row.canonical_baseline_document_count);
  const firstDate = row.canonical_baseline_first_date;
  const lastDate = row.canonical_baseline_last_date;
  const hasEvidence =
    row.canonical_baseline_price != null &&
    lineCount !== null &&
    documentCount !== null;
  if (!hasEvidence) return <span className="text-slate-400">–</span>;

  const documentLabel = `${documentCount.toLocaleString("de-AT")} ${
    documentCount === 1 ? "Beleg" : "Belege"
  }`;
  const positionLabel = `${lineCount.toLocaleString("de-AT")} Pos.`;
  const dateRange =
    firstDate && lastDate
      ? `${formatReferenceMonth(firstDate)}–${formatReferenceMonth(lastDate)}`
      : null;
  const referenceWindow = baselineKindShortLabel(
    row.canonical_baseline_kind,
  );
  const meta = [positionLabel, documentLabel, dateRange, referenceWindow]
    .filter(Boolean)
    .join(" · ");
  const evidenceDetails = meta;

  return (
    <div
      data-testid="reference-evidence"
      title={`Preisreferenz: ${baselineKindLabel(row.canonical_baseline_kind)} · ${evidenceDetails}`}
      className="text-right"
    >
      {/* Herkunft der Referenz nur noch beim Darüberfahren (#630). */}
      <div
        data-testid="reference-value"
        className="whitespace-nowrap text-slate-700"
      >
        {formatEuro(row.canonical_baseline_price)}
      </div>
      {lineCount < Math.max(2, row.price_anomaly_minimum_reference_count ?? 2) || documentCount < 2 ? (
        <div className="mt-1 text-[10px] text-amber-800">{documentCount < 2 ? "Nur ein früherer Beleg" : "Zu wenige frühere Positionen"}</div>
      ) : null}
    </div>
  );
}

function baselineKindShortLabel(
  kind: "12m" | "24m" | "alltime" | null,
): string {
  if (kind === "12m") return "12 M";
  if (kind === "24m") return "24 M";
  if (kind === "alltime") return "Gesamthistorie";
  return "–";
}

function formatReferenceMonth(value: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(value);
  return match ? `${match[2]}/${match[1].slice(2)}` : value;
}
